import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { useInventoryTags } from '@rematter/modules/trade/hooks/useInventoryTags';
import {
  humanReadableShortPricingUnit,
  MultiSelect,
  SelectableOptionProps,
} from '@rematter/ui-web';

import { InventoryTagsFieldCellProps } from '../types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';

type InventoryTagMetadata = {
  netQuantity?: number | null;
  unit: Unit;
};

type InventoryTagOption = SelectableOptionProps<string> & {
  metadata?: InventoryTagMetadata;
};

export const InventoryTagsFieldCell: React.FC<InventoryTagsFieldCellProps> = ({
  lineItem,
  onUpdateLineItem,
  disabled = false,
}) => {
  const {
    inventoryTagsOptions,
    inventoryTagOptionsByItemId,
    isInventoryTagsLoading,
  } = useInventoryTags();

  const t = useTranslations();

  const inventoryTagsForThisItemId = React.useMemo<InventoryTagOption[]>(
    () =>
      lineItem.itemId
        ? (inventoryTagOptionsByItemId?.get(lineItem.itemId)?.map((tag) => ({
            key: tag.id,
            label: tag.mnemonic,
            extraInfo: (
              <span className="text-right text-sm text-neutral/50">
                {tag.tagData?.netQuantity}{' '}
                {humanReadableShortPricingUnit(enumUnit.Pounds)}
              </span>
            ),
            metadata: {
              netQuantity: Number(tag.tagData?.netQuantity),
              unit: enumUnit.Pounds,
            },
          })) ?? [])
        : [],
    [inventoryTagOptionsByItemId, lineItem.itemId],
  );

  const handleInventoryTagChange = React.useCallback(
    (ids: string[]) => {
      const newAggregatedTags = ids.map((id) => {
        const existingTag = lineItem.aggregatedTags?.find((t) => t.id === id);

        if (existingTag) {
          return existingTag;
        }

        const inventoryTag = inventoryTagsOptions.find((tag) => tag.id === id);
        const netQuantity = Number(inventoryTag?.tagData?.netQuantity ?? 0);

        return {
          id,
          netQuantity,
          weights: {
            [enumLoadWeightType.Net]: {
              value: netQuantity,
              unit: enumUnit.Pounds,
              __typename: 'Weight' as const,
            },
            [enumLoadWeightType.Gross]: {
              value: netQuantity,
              unit: enumUnit.Pounds,
              __typename: 'Weight' as const,
            },
            [enumLoadWeightType.Tare]: {
              value: 0,
              unit: enumUnit.Pounds,
              __typename: 'Weight' as const,
            },
          },
        };
      });

      const sumOfNetQuantities = newAggregatedTags.reduce(
        (acc, tag) => acc + Number(tag.netQuantity),
        0,
      );

      onUpdateLineItem(lineItem.id, {
        aggregatedTags: newAggregatedTags,
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Net]: {
            value: sumOfNetQuantities,
            unit:
              lineItem.weights?.[enumLoadWeightType.Net]?.unit ??
              enumUnit.Pounds,
            __typename: 'Weight' as const,
          },
        },
      });
    },
    [
      inventoryTagsOptions,
      onUpdateLineItem,
      lineItem.id,
      lineItem.weights,
      lineItem.aggregatedTags,
    ],
  );

  const isDisabled = React.useMemo(
    () => disabled || lineItem.type === 'Raw',
    [disabled, lineItem.type],
  );

  const renderCustomInput = (selectedOptions?: InventoryTagOption[]) => {
    if (!selectedOptions?.length) {
      return null;
    }

    const firstOptionUnit = selectedOptions[0]?.metadata?.unit;

    if (selectedOptions.length === 1) {
      const metadata = selectedOptions[0]?.metadata;
      return (
        <>
          {selectedOptions[0]?.label} ({metadata?.netQuantity}{' '}
          {firstOptionUnit && humanReadableShortPricingUnit(firstOptionUnit)})
        </>
      );
    }

    const totalWeight = selectedOptions.reduce(
      (sum, option) => sum + (option.metadata?.netQuantity ?? 0),
      0,
    );

    return (
      <>
        {selectedOptions.length} tags ({totalWeight}{' '}
        {firstOptionUnit && humanReadableShortPricingUnit(firstOptionUnit)})
      </>
    );
  };

  return (
    <MultiSelect
      placeholder={t.loadsView.form.placeholders.inventoryTags}
      options={inventoryTagsForThisItemId}
      selectedKeys={lineItem.aggregatedTags?.map((tag) => tag.id)}
      isDisabled={isDisabled}
      isLoading={isInventoryTagsLoading}
      onSelectionChange={handleInventoryTagChange}
      className="@InventoryTagsFieldCell"
      customInput={renderCustomInput}
    />
  );
};
